//Global Imports
import JoditEditor from 'jodit-react';
import {
    CFormInput,
    CFormLabel,
    CFormSelect,
    CFormTextarea,
} from '@coreui/react';
import { useState } from 'react';

//File Imports
import { Language } from '../../../../language/English';
import { APP_LANGAUGE, LANGAUGE_NAME } from '../../../../Constant';
import { StringUtils } from '../../Types/types';
import DocumentTemplateTextShortDescription from '../../../../assets/images/document_template_text_shortDescription.webp';

//Component Imports
import {
    FieldHeadingWithHintText,
    FieldPreviewModal,
    HintText,
    LengthChecker,
} from '../../../../components';

interface DocumentTemplateTextProps {
    stringUtils: StringUtils;
    data: {
        documentTitle: string;
        useOtherLanguageText: boolean;
        otherTranslationData: Number[] | null;
        sourceDocumentTemplateTranslationId: string;
        documentText: string;
        documentDetailUrl: string;
        shortDescription: string;
    };
    errors: {
        documentTitle?: string;
        documentText?: string;
        sourceDocumentTemplateTranslationId?: string;
        documentTextPreviewRequired?: string;
        duplicatePlaceholders?: string;
        documentDetailUrl?: string;
        shortDescription?: string;
    };
    editor: React.RefObject<any>;
    editorConfig: any;
    language: number;
    name: string;
    useOtherLanguageCheckBoxChanged: (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => void;
    onLanguageSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onChageDocumentText: (newContent: string) => void;
    handleChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    onBlurUrlField: (e: React.FocusEvent<HTMLInputElement>) => void;
    handlePaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

const DocumentTemplateText: React.FC<DocumentTemplateTextProps> = ({
    stringUtils,
    data,
    errors,
    editor,
    editorConfig,
    language,
    name,
    handleChange,
    useOtherLanguageCheckBoxChanged,
    onLanguageSelectChange,
    onChageDocumentText,
    onBlurUrlField,
    onKeyDown,
    handlePaste,
}) => {
    const [fieldPreviewModalVisible, setFieldPreviewModalVisible] =
        useState(false);
    const [hintImage, setHintImage] = useState<string>('');

    const openHintModal = (imageSource: string) => {
        setHintImage(imageSource);
        setFieldPreviewModalVisible(true);
    };

    const getLanguageNameById = (id: number): string | undefined => {
        const langCode = Object.keys(APP_LANGAUGE).find(
            (key) => APP_LANGAUGE[key] === id,
        );

        return langCode ? LANGAUGE_NAME[langCode] : undefined;
    };
    return (
        <>
            {/* Document Title */}
            <CFormLabel
                htmlFor={stringUtils.convertToCamelCase(
                    Language.DOCUMENT_TITLE,
                )}
            >
                {Language.DOCUMENT_TITLE +
                    ' ' +
                    Language.IN +
                    ' ' +
                    LANGAUGE_NAME[language]}
            </CFormLabel>
            <CFormInput
                value={data.documentTitle}
                invalid={!!errors.documentTitle}
                feedback={errors.documentTitle}
                type="text"
                name={stringUtils.convertToCamelCase(Language.DOCUMENT_TITLE)}
                placeholder={
                    Language.ENTER_DOCUMENT_TITLE +
                    ' ' +
                    Language.IN.toLowerCase() +
                    ' ' +
                    LANGAUGE_NAME[language].toLowerCase()
                }
                onChange={(e) => handleChange(e)}
                maxLength={400}
            />
            <LengthChecker
                currentLength={data.documentTitle.length}
                maxLength={400}
            />

            {/* URL */}
            <CFormLabel
                htmlFor={stringUtils.convertToCamelCase(
                    Language.DOCUMENT_DETAIL_URL,
                )}
            >
                {Language.URL}
            </CFormLabel>
            <CFormInput
                type="text"
                placeholder={`Ex. ${name.toLowerCase().split(' ').join('-')}`}
                value={data.documentDetailUrl}
                invalid={!!errors.documentDetailUrl}
                feedback={errors.documentDetailUrl}
                onChange={(e) => handleChange(e)}
                onBlur={(e) => onBlurUrlField(e)}
                id={stringUtils.convertToCamelCase(
                    Language.DOCUMENT_DETAIL_URL,
                )}
                name={stringUtils.convertToCamelCase(
                    Language.DOCUMENT_DETAIL_URL,
                )}
                maxLength={200}
            />
            <LengthChecker
                currentLength={data.documentDetailUrl.length}
                maxLength={200}
            />

            {data.otherTranslationData &&
                data.otherTranslationData?.length > 0 && (
                    <div className="d-flex align-items-center">
                        <CFormLabel
                            style={{
                                marginBottom: 0,
                            }}
                            htmlFor={stringUtils.convertToCamelCase(
                                Language.USE_OTHER_LANGUAGE_TEXT,
                            )}
                        >
                            {Language.USE_OTHER_LANGUAGE_TEXT}
                        </CFormLabel>
                        <input
                            type="checkbox"
                            checked={data?.useOtherLanguageText || false}
                            className="ms-2"
                            onChange={useOtherLanguageCheckBoxChanged}
                        />
                    </div>
                )}

            {data?.useOtherLanguageText && (
                <>
                    <div
                        id="sourceDocumentTemplateTranslationId"
                        className="d-flex align-items-center mt-3"
                    >
                        <CFormLabel
                            className="mb-0 me-2"
                            htmlFor={stringUtils.convertToCamelCase(
                                Language.WHICH_DOCUMENT_TEMPLATE_TRANSLATION,
                            )}
                        >
                            {Language.WHICH_DOCUMENT_TEMPLATE_TRANSLATION}
                        </CFormLabel>
                        <CFormSelect
                            className="w-auto"
                            value={
                                data.sourceDocumentTemplateTranslationId || ''
                            }
                            onChange={onLanguageSelectChange}
                        >
                            <option value="">
                                {Language.SELECT_DOCUMENT_TEMPLATE_TRANSLATION}
                            </option>
                            {data.otherTranslationData &&
                                data.otherTranslationData.map((langId: any) => (
                                    <option key={langId} value={langId}>
                                        {getLanguageNameById(langId)}
                                    </option>
                                ))}
                        </CFormSelect>
                    </div>
                    {errors.sourceDocumentTemplateTranslationId && (
                        <div className="alert alert-danger">
                            {errors.sourceDocumentTemplateTranslationId}
                        </div>
                    )}
                </>
            )}

            {!data.useOtherLanguageText && (
                <>
                    <CFormLabel
                        style={{
                            marginBottom: 0,
                        }}
                        htmlFor={stringUtils.convertToCamelCase(
                            Language.DOCUMENT_TEXT,
                        )}
                        className="mt-3"
                    >
                        {Language.DOCUMENT_TEXT}
                    </CFormLabel>
                    <HintText marginBottom="10px">
                        {Language.DOCUMENT_TEXT_HINT}
                    </HintText>
                    <div id="documentText">
                        <JoditEditor
                            ref={editor}
                            value={data.documentText}
                            config={editorConfig}
                            onBlur={(newContent) =>
                                onChageDocumentText(newContent)
                            }
                        />
                    </div>
                    {errors.documentText && (
                        <div className="alert alert-danger">
                            {Language.DOCUMENT_TEXT_REQUIRED}
                        </div>
                    )}
                    {errors.documentTextPreviewRequired && (
                        <div className="alert alert-danger">
                            {Language.PREVIEW_END_REQUIRED}
                        </div>
                    )}
                    {errors.duplicatePlaceholders && (
                        <div className="alert alert-danger">
                            {errors.duplicatePlaceholders}
                        </div>
                    )}
                </>
            )}

            {/* Short Description */}
            {/* <CFormLabel
                style={{ marginBottom: 0 }}
                htmlFor={stringUtils.convertToCamelCase(
                    Language.SHORT_DESCRIPTION,
                )}
                className="mt-3"
            >
                {Language.SHORT_DESCRIPTION_FOR_LISTING_PAGE}
            </CFormLabel> */}
            <FieldHeadingWithHintText
                stringUtils={stringUtils}
                heading={Language.SHORT_DESCRIPTION_FOR_LISTING_PAGE}
                openModal={() => {
                    openHintModal(DocumentTemplateTextShortDescription);
                }}
            />
            <CFormTextarea
                placeholder={Language.ENTER_SHORT_DESCRIPTION}
                onKeyDown={onKeyDown}
                onChange={(e) => handleChange(e)}
                onPaste={handlePaste}
                value={data.shortDescription}
                invalid={!!errors.shortDescription}
                feedback={errors.shortDescription}
                maxLength={1000}
                style={{ height: 180, marginTop: '10px' }}
                name={stringUtils.convertToCamelCase(
                    Language.SHORT_DESCRIPTION,
                )}
            />
            <LengthChecker
                currentLength={data.shortDescription.length}
                maxLength={1000}
            />

            <FieldPreviewModal
                visible={fieldPreviewModalVisible}
                onClose={() => setFieldPreviewModalVisible(false)}
                imageSrc={hintImage}
            />
        </>
    );
};

export default DocumentTemplateText;
